import { ChatMessage } from '../types';
import type { AgentContext, SessionStatus } from '../../../types';

export type SessionEventType =
  | 'chunk'
  | 'system_context'
  | 'user_message'
  | 'assistant_message'
  | 'assistant_message_truncated'
  | 'tool_call'
  | 'tool_awaiting_input'
  | 'tool_result'
  | 'status_change'
  | 'error';

export type SessionStreamEvent = {
  type: SessionEventType;
  seq?: number;
  created_at?: string;
  text?: string;
  delta?: string;
  tool_call_id?: string;
  tool_name?: string;
  args?: Record<string, any>;
  reason?: 'approval' | 'output';
  result?: Record<string, any>;
  status?: SessionStatus;
  message?: string;
  code?: string | null;
  context?: Record<string, unknown>;
};

export type SessionTranslationState = {
  chunkActive: boolean;
  pendingUserEcho: number;
  awaitingOutputToolId?: string;
  pendingContext?: AgentContext;
};

export const INITIAL_TRANSLATION_STATE: SessionTranslationState = {
  chunkActive: false,
  pendingUserEcho: 0,
};

const asStringArray = (value: unknown): string[] | undefined =>
  Array.isArray(value) && value.every((item) => typeof item === 'string')
    ? value
    : undefined;

export const toAgentContext = (
  context?: Record<string, unknown>,
): AgentContext | undefined => {
  if (!context) return undefined;
  const environmentIds = asStringArray(context.environment_ids);
  const incidentIds = asStringArray(context.incident_ids);
  const assetIds = asStringArray(context.asset_ids);
  const testIds = asStringArray(context.test_ids);
  if (
    !environmentIds?.length &&
    !incidentIds?.length &&
    !assetIds?.length &&
    !testIds?.length
  ) {
    return undefined;
  }
  return { environmentIds, incidentIds, assetIds, testIds };
};

export const ensureStreamingAssistant = (
  prev: ChatMessage[],
): ChatMessage[] => {
  const last = prev[prev.length - 1];
  if (last && last.role === 'assistant' && !last.hidden) {
    return prev;
  }
  return [
    ...prev,
    {
      content: '',
      role: 'assistant',
      parts: [{ type: 'step-start' }],
      toolInvocations: [],
    },
  ];
};

export const appendAssistantText = (
  prev: ChatMessage[],
  delta: string,
): ChatMessage[] => {
  const withAssistant = ensureStreamingAssistant(prev);
  const lastMessage = withAssistant[withAssistant.length - 1];
  const lastPart = lastMessage.parts[lastMessage.parts.length - 1];
  const parts = [...lastMessage.parts];
  if (lastPart && lastPart.type === 'text') {
    parts[parts.length - 1] = { type: 'text', text: lastPart.text + delta };
  } else {
    parts.push({ type: 'text', text: delta });
  }
  return [
    ...withAssistant.slice(0, -1),
    { ...lastMessage, content: lastMessage.content + delta, parts },
  ];
};

export const addAssistantMessageText = (
  prev: ChatMessage[],
  text: string,
  createdAt?: string,
): ChatMessage[] => {
  const withAssistant = ensureStreamingAssistant(prev);
  const lastMessage = withAssistant[withAssistant.length - 1];
  return [
    ...withAssistant.slice(0, -1),
    {
      ...lastMessage,
      content: lastMessage.content + text,
      parts: [...lastMessage.parts, { type: 'text', text }],
      createdAt: lastMessage.createdAt ?? createdAt,
    },
  ];
};

export const stampAssistantCreatedAt = (
  prev: ChatMessage[],
  createdAt?: string,
): ChatMessage[] => {
  if (!createdAt) return prev;
  const last = prev[prev.length - 1];
  if (!last || last.role !== 'assistant' || last.createdAt) return prev;
  return [...prev.slice(0, -1), { ...last, createdAt }];
};

export const appendUserMessage = (
  prev: ChatMessage[],
  text: string,
  createdAt?: string,
  context?: AgentContext,
): ChatMessage[] => [
  ...prev,
  {
    content: text,
    role: 'user',
    parts: [{ type: 'text', text }],
    createdAt,
    ...(context && { context }),
  },
];

const withContextOnLastUserMessage = (
  messages: ChatMessage[],
  context?: AgentContext,
): ChatMessage[] => {
  if (!context) return messages;
  const last = messages[messages.length - 1];
  if (!last || last.role !== 'user' || last.context) return messages;
  return [...messages.slice(0, -1), { ...last, context }];
};

export const upsertToolInvocation = (
  prev: ChatMessage[],
  toolCallId: string,
  patch: Record<string, any>,
): ChatMessage[] => {
  const idx = prev.findIndex((message) =>
    message.parts.find(
      (part) =>
        part.type === 'tool-invocation' &&
        part.toolInvocation.toolCallId === toolCallId,
    ),
  );
  if (idx >= 0) {
    const updatedParts = prev[idx].parts.map((part) =>
      part.type === 'tool-invocation' &&
      part.toolInvocation.toolCallId === toolCallId
        ? { ...part, toolInvocation: { ...part.toolInvocation, ...patch } }
        : part,
    );
    return prev.map((message, i) =>
      i === idx ? { ...message, parts: updatedParts } : message,
    );
  }
  const withAssistant = ensureStreamingAssistant(prev);
  const lastMessage = withAssistant[withAssistant.length - 1];
  return [
    ...withAssistant.slice(0, -1),
    {
      ...lastMessage,
      parts: [
        ...lastMessage.parts,
        {
          type: 'tool-invocation',
          toolInvocation: { toolCallId, state: 'call', ...patch },
        },
      ],
    },
  ];
};

export type TranslateResult = {
  messages: ChatMessage[];
  state: SessionTranslationState;
};

export const translateSessionEvent = (
  messages: ChatMessage[],
  event: SessionStreamEvent,
  state: SessionTranslationState,
): TranslateResult => {
  const toolCallId = event.tool_call_id || '';
  switch (event.type) {
    case 'chunk':
      return {
        messages: appendAssistantText(messages, event.delta || ''),
        state: { ...state, chunkActive: true },
      };
    case 'system_context':
      return {
        messages,
        state: {
          ...state,
          pendingContext: toAgentContext(event.context),
        },
      };
    case 'user_message':
      // The optimistic local echo is reconciled against the first server
      // user_message so we don't render the prompt twice.
      if (state.pendingUserEcho > 0) {
        return {
          messages: withContextOnLastUserMessage(
            messages,
            state.pendingContext,
          ),
          state: {
            ...state,
            pendingUserEcho: state.pendingUserEcho - 1,
            pendingContext: undefined,
          },
        };
      }
      return {
        messages: appendUserMessage(
          messages,
          event.text || '',
          event.created_at,
          state.pendingContext,
        ),
        state: { ...state, pendingContext: undefined },
      };
    case 'assistant_message':
    case 'assistant_message_truncated':
      // A streamed message was already built from chunks; skip the final
      // consolidated copy and just reset the streaming flag (but stamp the
      // server time onto the streamed message).
      if (state.chunkActive) {
        return {
          messages: stampAssistantCreatedAt(messages, event.created_at),
          state: { ...state, chunkActive: false },
        };
      }
      return {
        messages: event.text
          ? addAssistantMessageText(messages, event.text, event.created_at)
          : stampAssistantCreatedAt(messages, event.created_at),
        state,
      };
    case 'tool_call':
      return {
        messages: upsertToolInvocation(messages, toolCallId, {
          toolName: event.tool_name,
          args: event.args,
          state: 'call',
          type: 'start',
        }),
        state,
      };
    case 'tool_awaiting_input':
      // Only `tool_call` may open a new invocation (it carries the tool name).
      // Without an id we can't match an existing one, so skip rather than
      // create an orphan invocation with no name.
      if (!toolCallId) {
        return { messages, state };
      }
      return {
        messages: upsertToolInvocation(messages, toolCallId, {
          state: 'call',
          type: event.reason === 'output' ? 'output' : 'approval',
          interruptId: event.tool_call_id,
          message: event.message,
          ...(event.args ? { args: event.args } : {}),
          ...(event.reason !== 'output' ? { didAwaitApproval: true } : {}),
        }),
        state:
          event.reason === 'output'
            ? { ...state, awaitingOutputToolId: event.tool_call_id }
            : state,
      };
    case 'tool_result':
      if (!toolCallId) {
        return { messages, state };
      }
      // Clear the transient `start` marker so completed FE display tools
      // (e.g. lineage graph) render their content instead of a placeholder.
      // Stamp the server time so tool-only turns (e.g. user_actions_display)
      // still get a message timestamp.
      return {
        messages: stampAssistantCreatedAt(
          upsertToolInvocation(messages, toolCallId, {
            state: 'result',
            type: undefined,
            result: {
              result: event.result || {},
              isError:
                event.result?.isError === true ||
                event.result?.isError === 'True',
              error:
                typeof event.result?.error === 'string'
                  ? event.result.error
                  : undefined,
              nextSteps:
                typeof event.result?.nextSteps === 'string'
                  ? event.result.nextSteps
                  : undefined,
            },
          }),
          event.created_at,
        ),
        // Exit "awaiting output" once the matching tool completes, otherwise
        // the UI stays in input mode after the result lands.
        state:
          state.awaitingOutputToolId === toolCallId
            ? { ...state, awaitingOutputToolId: undefined }
            : state,
      };
    default:
      return { messages, state };
  }
};
